import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Eye, Users, Award, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SectionBubbles from '../components/SectionBubbles';
import SEO from '../components/SEO';

const About = () => {
  const { t } = useTranslation();
  const [activeAdj, setActiveAdj] = useState(null);
  const adjectives = t('about.adjectives', { returnObjects: true });

  const values = [
    { icon: Award, title: "Excellence", description: "Uncompromising quality standards in every service delivery" },
    { icon: Users, title: "Accountability", description: "Transparent operations with measurable performance metrics" },
    { icon: Target, title: "Precision", description: "Systematic processes ensuring consistent outcomes" },
  ];

  return (
    <div className="page-wrapper">
      <SEO 
        title={t('seo.about.title')}
        description={t('seo.about.description')}
        keywords={t('seo.about.keywords')}
        canonical="https://dsotocleaning.com/about"
      />
      
      <Navigation />
      
      <section className="about-hero">
        <div className="about-hero-overlay"></div>
        <div className="about-hero-content">
          <h1 className="about-hero-title">{t('about.hero_title')}</h1>
          <p className="about-hero-subtitle">{t('about.hero_subtitle')}</p>
        </div>
      </section>

      <section className="section-padding">
        <SectionBubbles />
        <div className="container">
          <div className="content-grid">
            <div className="content-text">
              <h2 className="section-title">{t('about.section_title')}</h2>
              <p className="section-description">{t('about.section_p1')}</p>
              <p className="section-description">{t('about.section_p2')}</p>
            </div>
            <div className="content-image">
              <img 
                src="https://customer-assets.emergentagent.com/job_0b1e91d0-9c1d-440a-9b84-aeb555888e53/artifacts/aqoxar6e_12-Feed-D_Soto-Febrero-2026.jpg" 
                alt="D Soto Cleaning Leadership Team" 
                className="image-rounded"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Redesigned Mission & Vision */}
      <section className="mv-section" data-testid="mission-vision-section">
        <div className="container">
          <div className="mv-grid">
            <div className="mv-card" data-testid="mission-card">
              <div className="mv-accent"></div>
              <div className="mv-card-inner">
                <div className="mv-icon-wrap"><Target size={28} /></div>
                <h3 className="mv-label">{t('about.mission_title')}</h3>
                <p className="mv-text">{t('about.mission_text')}</p>
              </div>
            </div>
            <div className="mv-card" data-testid="vision-card">
              <div className="mv-accent"></div>
              <div className="mv-card-inner">
                <div className="mv-icon-wrap"><Eye size={28} /></div>
                <h3 className="mv-label">{t('about.vision_title')}</h3>
                <p className="mv-text">{t('about.vision_text')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding">
        <SectionBubbles />
        <div className="container">
          <div className="enterprise-section">
            <div className="enterprise-block">
              <h2 className="enterprise-title">{t('about.mission_block_title')}</h2>
              <h3 className="enterprise-subtitle">{t('about.mission_block_subtitle')}</h3>
              <p className="enterprise-description">{t('about.mission_block_desc')}</p>
              <div className="enterprise-list-title">{t('about.mission_list_title')}</div>
              <ul className="enterprise-list">
                {t('about.mission_list', { returnObjects: true }).map((item, i) => <li key={i}>{item}</li>)}
              </ul>
              <p className="enterprise-footer-text">{t('about.mission_footer')}</p>
            </div>

            <div className="enterprise-block">
              <h2 className="enterprise-title">{t('about.vision_block_title')}</h2>
              <h3 className="enterprise-subtitle">{t('about.vision_block_subtitle')}</h3>
              <p className="enterprise-description">{t('about.vision_block_desc')}</p>
              <div className="enterprise-list-title">{t('about.vision_list_title')}</div>
              <ul className="enterprise-list">
                {t('about.vision_list', { returnObjects: true }).map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </div>

            <div className="enterprise-block adjectives-block">
              <h2 className="enterprise-title">{t('about.adj_title')}</h2>
              <p className="adjectives-intro">{t('about.adj_intro')}</p>
              <div className="adjectives-grid" data-testid="adjectives-grid">
                {adjectives.map((adj, i) => (
                  <div key={i} className="adjective-wrapper">
                    <button
                      className={`adjective-item${activeAdj === i ? ' adjective-active' : ''}`}
                      onClick={() => setActiveAdj(activeAdj === i ? null : i)}
                      data-testid={`adjective-btn-${i}`}
                    >
                      <span>{adj.title}</span>
                      <ChevronDown size={18} className={`adjective-chevron${activeAdj === i ? ' rotated' : ''}`} />
                    </button>
                    <div className={`adjective-desc${activeAdj === i ? ' adjective-desc-open' : ''}`} data-testid={`adjective-desc-${i}`}>
                      <p>{adj.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding">
        <SectionBubbles />
        <div className="container">
          <h2 className="section-title text-center">{t('about.values_title')}</h2>
          <p className="section-description text-center centered-text mb-12">{t('about.values_desc')}</p>
          <div className="values-grid">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="value-card">
                  <CardContent className="value-card-content">
                    <div className="value-icon-wrapper">
                      <IconComponent size={36} />
                    </div>
                    <h3 className="value-title">{value.title}</h3>
                    <p className="value-description">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="stats-section section-padding bg-light-gray">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">{t('about.stats.years')}</div>
              <div className="stat-label">{t('about.stats.years_label')}</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{t('about.stats.team')}</div>
              <div className="stat-label">{t('about.stats.team_label')}</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{t('about.stats.cities')}</div>
              <div className="stat-label">{t('about.stats.cities_label')}</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{t('about.stats.vehicles')}</div>
              <div className="stat-label">{t('about.stats.vehicles_label')}</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">1000s</div>
              <div className="stat-label">Vehicles Serviced Monthly</div>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section section-padding">
        <div className="container text-center">
          <h2 className="section-title text-white">{t('about.cta_title')}</h2>
          <p className="section-description text-white centered-text mb-8">{t('about.cta_desc')}</p>
          <Link to="/contact">
            <Button size="lg" className="cta-button-white">
              {t('about.cta_btn')} <ArrowRight className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
